import { inject, observer } from "mobx-react";
import { injectIntl } from "react-intl";
import { useLocation } from "react-router";
import React, { useState, useEffect, useRef } from "react";
import { Box, Tab, Stack, IconButton } from "@mui/material";
import { Icon } from "@iconify/react";
import ReportDaily from "./ReportDaily";
import ReportWeekly from "./ReportWeekly";
import BottomNavigation from "@/views/CommonComponent/BottomNavigation";
import { NativeToJsBridge, NativeToJsEventName } from "@/nativebridge/NativeToJs";
/* styled */
import { CalendarBox } from "@/common/Styled/CalendarStyles";
import { TitleStyle, CustomTabs } from "@/common/Styled/CommonStyled";
import { DateTab } from "../Pronunciation/Styled/PronunciationDataStyled";
import { Root } from "@/common/Styled/CommonStyled";
import ShareFileTypeDialog from "@/views/CommonComponent/ShareFileTypeDialog";
import {
  captureElementAndShare,
  isPdfShareSupportedInUserAgent,
} from "@/common/util/shareElementCapture.util";

/* calendar */
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import advancedFormat from "dayjs/plugin/advancedFormat";
import "dayjs/locale/ko";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import moment from "moment";
import { isReport } from "@/nativebridge/JsToNative";

import SecretPokoro from "@/common/Images/SecretPokoro.png"
import {useToast} from "@/components/useToast";
import {DATE_UTIL} from "../../common/util/date.util";

dayjs.extend(isoWeek);
dayjs.extend(advancedFormat);
dayjs.locale("ko");

function Report(props) {
  const location = useLocation();
  const {
    intl,
    authStore,
    userStore,
    loadingStore,
    reportStore,
    languageStore,
    homeStore,
    pronunciationStore,
  } = props;
  const { selectedTab } = reportStore;
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarKey, setCalendarKey] = useState(0);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const calendarRef = useRef();
  const shareCaptureRef = useRef(null);
  const { showDeleteToast } = useToast();

  const handleShareIconClick = () => setShareDialogOpen(true);
  const handleShareDialogClose = () => setShareDialogOpen(false);
  const isShareDisabled =
    selectedTab === "daily"
      ? (reportStore.dailyReport?.normalDialogCount === 0 && reportStore.dailyReport?.contentDialogCount === 0)
      : !reportStore.weeklyReport?.conversation;
  const handleShareExport = async (fileType) => {
    setShareDialogOpen(false);

    const el = shareCaptureRef.current;
    if (!el) return;

    const tab = reportStore.selectedTab;
    const start = reportStore.startDatetime?.format
      ? DATE_UTIL.getTimeZoneDate(reportStore.startDatetime, "YYYY.MM.DD")
      : "date";
    const end = reportStore.endDatetime?.format
      ? DATE_UTIL.getTimeZoneDate(reportStore.endDatetime, "YYYY.MM.DD")
      : "";

    const baseName =
      tab === "weekly"
        ? `pokoro_report_${start}~${end}`
        : `pokoro_report_${start}`;

    reportStore.setIsReportDownloadLoading(true);
    try {
      await captureElementAndShare(el, fileType, baseName);
    } finally {
      reportStore.setIsReportDownloadLoading(false);
    }
  };

  useEffect(() => {
    isReport(true);
    return () => {
      isReport(false);
    };
  }, []);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    reportStore.getDailyReport(
      authStore.loginUser.id,
      userStore.selectedDevice.deviceId
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    NativeToJsBridge.instance.addEventListener(
        NativeToJsEventName.onSetPushAgree,
        async (event) => {
          const isAgree = event.isAgree;
          console.log("========NativeToJs=========== isAgree=" + isAgree);
          userStore.updateUserPushAgreement(authStore.loginUser.id, isAgree, () => {
            if (!isAgree) {
              const now = new Date();
              const formattedDate = `${now.getFullYear()}. ${String(now.getMonth() + 1).padStart(2, '0')}. ${String(now.getDate()).padStart(2, '0')}`;
              showDeleteToast(
                  <span style={{ whiteSpace: "pre-line" }}>
                    {intl.formatMessage({ id: "Toast_marketing_notification_off" })} {/* 푸시 알림을 거부했습니다. */}
                    &nbsp;{formattedDate}
                  </span>
              );
            }
          });
        }
    );

    return () => {
      NativeToJsBridge.instance.removeEventListener(
          NativeToJsEventName.onSetPushAgree
      );
    };
  }, []);

  const handleTabChange = (event, newTab) => {
    reportStore.setSelectedTab(newTab);
    reportStore.resetDate(reportStore.calendarDate);

    if (reportStore.selectedTab === "daily") {
      reportStore.getDailyReport(
        authStore.loginUser.id,
        userStore.selectedDevice.deviceId
      );
    } else {
      reportStore.getWeeklyReport(
        authStore.loginUser.id,
        userStore.selectedDevice.deviceId,
        languageStore.getLanguageString
      );
    }
  };

  const handleArrowClick = (direction) => {
    reportStore.setDateByDirection(direction);
    if (selectedTab === "daily") {
      reportStore.getDailyReport(
        authStore.loginUser.id,
        userStore.selectedDevice.deviceId
      );
    } else {
      reportStore.getWeeklyReport(
        authStore.loginUser.id,
        userStore.selectedDevice.deviceId,
        languageStore.getLanguageString
      );
    }

    homeStore.setSelectedCalendarDate(reportStore.calendarDate.toDate());
    pronunciationStore.setDate(dayjs(reportStore.calendarDate));
  };

  const handleDateChange = (date) => {
    if (date) {
      setShowCalendar(false);
      document.body.style.overflow = "auto";

      reportStore.setDate(dayjs(date));
      if (selectedTab === "daily") {
        reportStore.getDailyReport(
          authStore.loginUser.id,
          userStore.selectedDevice.deviceId
        );
      } else {
        reportStore.getWeeklyReport(
          authStore.loginUser.id,
          userStore.selectedDevice.deviceId,
          languageStore.getLanguageString
        );
      }

      homeStore.setSelectedCalendarDate(date);
      pronunciationStore.setDate(dayjs(reportStore.calendarDate));
    }
  };

  const getDateDisplay = () => {
    if (selectedTab === "daily") {
      return reportStore.startDatetime
        .locale(languageStore.language)
        .format("YYYY. MM. DD (ddd)");
    }

    if (selectedTab === "weekly") {
      return `${reportStore.startDatetime.format(
        "YYYY. MM. DD"
      )} ~ ${reportStore.endDatetime.format("YYYY. MM. DD")}`;
    }

    return "";
  };

  const isNextButtonDisabled = () => {
    const now = dayjs();
    if (selectedTab === "daily") {
      return reportStore.startDatetime.isSame(now, "day");
    } else if (selectedTab === "weekly") {
      return reportStore.startDatetime.isSame(
        now.subtract(1, "week").startOf("isoWeek"),
        "day"
      );
    }
    return false;
  };

  /* 캘린더 외부 클릭 시 닫힘 */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
        document.body.style.overflow = "auto";
      }
    };
    if (showCalendar) {
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "auto";
    };
  }, [showCalendar]);

  useEffect(() => {
    loadingStore.setIsLoading(reportStore.getIsLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportStore.getIsLoading]);

  const [tabValue, setTabValue] = useState("report");

  // 데이터 있는 날짜 표시
  const tileContent = ({ date }) => {
    if (reportStore.reportCalendarDateList.includes(moment(date).format("YYYY-MM-DD"))) {
      return (
          <span
              aria-label="데이터 있는 날짜 표시"
              style={{
                width: "4px",
                height: "4px",
                background: "#FF993C",
                borderRadius: "100px",
                position: "absolute",
                bottom: 6
              }}
          />
      );
    }
    return null;
  };

  const handleChangeActiveDate = (event) => {
    reportStore.getReportCalendar(
      authStore.loginUser.id,
      userStore.selectedDevice,
      event.activeStartDate.toISOString()
    ).then(() => {
      setCalendarKey((prevKey) => prevKey + 1);
    });
  };

  const handleOpenCalendar = () => {
    setShowCalendar(true);

    reportStore.getReportCalendar(
      authStore.loginUser.id,
      userStore.selectedDevice,
      reportStore.startDatetime.toISOString()
    ).then(() => {
      setCalendarKey((prevKey) => prevKey + 1);
    });
  };

  return (
    <Root>
      {tabValue === "report" && (
        <>
          <TitleStyle>
            <Icon icon="noto:bar-chart" />{" "}
            {intl.formatMessage({ id: "report_title" })}
            <IconButton
              onClick={handleShareIconClick}
              disabled={isShareDisabled}
              sx={{ marginLeft: "auto" }}
            >
              <Icon icon="mingcute:share-2-line" fontSize={24} />
            </IconButton>
          </TitleStyle>

          <Box>
            <CustomTabs
              value={selectedTab}
              onChange={handleTabChange}
              centered
              variant="fullWidth"
            >
              <Tab
                value="daily"
                label={intl.formatMessage({ id: "daily_report" })}
              />
              <Tab
                value="weekly"
                label={intl.formatMessage({ id: "weekly_report" })}
              />
            </CustomTabs>
          </Box>

          <Stack ref={shareCaptureRef} gap={1} mb={9} sx={{ background: "#fbfbfb" }}>
            <DateTab pt={1}>
              <IconButton
                size="large"
                onClick={() => handleArrowClick("previous")}
              >
                <Icon icon={"iconamoon:arrow-left-2-thin"} />
              </IconButton>

              <span
                onClick={handleOpenCalendar}
                style={{ cursor: "pointer" }}
              >
                {getDateDisplay()}
              </span>

              <IconButton
                size="large"
                onClick={() => handleArrowClick("next")}
                disabled={isNextButtonDisabled()}
              >
                <Icon icon={"iconamoon:arrow-right-2-thin"} />
              </IconButton>

              {showCalendar && (
                <>
                  <div
                    style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                      zIndex: 105,
                    }}
                  />

                  <CalendarBox ref={calendarRef} className="data">
                    <Calendar
                      locale={languageStore.language}
                      onActiveStartDateChange={handleChangeActiveDate}
                      onChange={handleDateChange}
                      value={reportStore.calendarDate.toDate()}
                      formatDay={(locale, date) => moment(date).format("D")}
                      tileContent={tileContent}
                      calendarType="gregory"
                      showNeighboringMonth={false}
                      tileDisabled={({ activeStartDate, date, view }) => {
                        const now = new Date();
                        const today = new Date(
                          now.getFullYear(),
                          now.getMonth(),
                          now.getDate(),
                          0,
                          0,
                          0
                        );

                        if (selectedTab === "weekly") {
                          return (
                            dayjs(date).add(7, "day").startOf("isoWeek") >
                            dayjs(today).startOf("isoWeek")
                          );
                        } else {
                          return date > today;
                        }
                      }}
                    />
                  </CalendarBox>
                </>
              )}
            </DateTab>
            {selectedTab === "daily" ? <ReportDaily /> : <ReportWeekly />}
          </Stack>
          <ShareFileTypeDialog
            open={shareDialogOpen}
            onClose={handleShareDialogClose}
            onExport={handleShareExport}
            showPdf={isPdfShareSupportedInUserAgent()}
          />
        </>
      )}
      <BottomNavigation
        value={tabValue}
        handleChangeTab={(event, newValue) => setTabValue(newValue)}
      />
    </Root>
  );
}

export default injectIntl(
  inject(
    "authStore",
    "userStore",
    "loadingStore",
    "reportStore",
    "languageStore",
    "homeStore",
    "pronunciationStore",
  )(observer(Report))
);
